/**
 * Agent Dossier PDF generator — client-side, jsPDF based.
 *
 * Produces a board-ready, "cryptographically signed" snapshot of an
 * agent: identity, ownership, model binding, policies, framework
 * mapping, KPI snapshot, and an excerpt of the agent's audit chain.
 *
 * The signature is a SHA-256 over the snapshot payload; nothing actually
 * signs the file — this is presentation-only. (The next ingestion phase
 * will route this through a server-side signer.)
 */
import jsPDF from "jspdf";

const COLORS = {
  ink: [12, 22, 41],
  inkSecondary: [76, 90, 117],
  inkDisabled: [148, 159, 181],
  accent: [10, 76, 144],     // tralenex blue
  gold: [196, 153, 27],
  risk: [194, 36, 36],
  low: [22, 122, 86],
  border: [216, 222, 232],
  raised: [248, 250, 253],
  inset: [241, 245, 250],
};

const PAGE = { width: 210, height: 297, margin: 16 };

function setColor(doc, c, kind = "text") {
  if (kind === "text") doc.setTextColor(c[0], c[1], c[2]);
  else if (kind === "fill") doc.setFillColor(c[0], c[1], c[2]);
  else if (kind === "draw") doc.setDrawColor(c[0], c[1], c[2]);
}

function sectionHeading(doc, y, text) {
  setColor(doc, COLORS.inkDisabled, "text");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(text.toUpperCase(), PAGE.margin, y);
  setColor(doc, COLORS.border, "draw");
  doc.setLineWidth(0.2);
  doc.line(PAGE.margin, y + 1.5, PAGE.width - PAGE.margin, y + 1.5);
  return y + 6;
}

function kvRow(doc, y, label, value) {
  setColor(doc, COLORS.inkSecondary, "text");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(label, PAGE.margin, y);
  setColor(doc, COLORS.ink, "text");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(value ?? "—"), PAGE.margin + 52, y, { maxWidth: PAGE.width - PAGE.margin - 52 - PAGE.margin });
  return y + 5.5;
}

function paragraph(doc, y, text, opts = {}) {
  if (!text) return y;
  setColor(doc, opts.color || COLORS.ink, "text");
  doc.setFont("helvetica", opts.bold ? "bold" : "normal");
  doc.setFontSize(opts.size || 10);
  const maxWidth = PAGE.width - PAGE.margin * 2;
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, PAGE.margin, y);
  return y + lines.length * (opts.lineHeight || 4.6) + 2;
}

function chipsRow(doc, y, items, color = COLORS.accent) {
  if (!items?.length) return y;
  const padX = 2.2;
  const padY = 1.4;
  const gap = 2.2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  let cursorX = PAGE.margin;
  const maxX = PAGE.width - PAGE.margin;
  items.forEach((label) => {
    const w = doc.getTextWidth(label) + padX * 2;
    if (cursorX + w > maxX) {
      cursorX = PAGE.margin;
      y += 7;
    }
    setColor(doc, color, "fill");
    doc.roundedRect(cursorX, y - 4, w, 5.5, 1.2, 1.2, "F");
    setColor(doc, [255, 255, 255], "text");
    doc.text(label, cursorX + padX, y - 0.2);
    cursorX += w + gap;
  });
  return y + 5;
}

async function fingerprint(payload) {
  if (typeof window === "undefined" || !window.crypto?.subtle) {
    return "FP-CLIENT-" + Math.random().toString(16).slice(2, 14).toUpperCase();
  }
  const data = new TextEncoder().encode(JSON.stringify(payload));
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest)).slice(0, 8).map((b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
}

function fmtAud(n) {
  if (!n) return "A$0";
  const a = Math.abs(n);
  if (a >= 1e6) return `A$${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `A$${Math.round(n / 1e3)}k`;
  return `A$${Math.round(n)}`;
}

export async function generateAgentDossier({ agent, tenant, policies = [], auditEntries = [], opportunity, request }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const now = new Date();

  // ── Cover ──
  setColor(doc, COLORS.accent, "fill");
  doc.rect(0, 0, PAGE.width, 36, "F");
  setColor(doc, [255, 255, 255], "text");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("ALGOS · TRALENEX  ·  Enterprise AI Lifecycle Management", PAGE.margin, 11);
  doc.setFontSize(20);
  doc.text("Agent Dossier", PAGE.margin, 22);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`${tenant?.name || tenant?.label || tenant?.id || "—"}`, PAGE.margin, 30);
  doc.text(now.toISOString().slice(0, 19).replace("T", " ") + " UTC", PAGE.width - PAGE.margin, 30, { align: "right" });

  let y = 46;

  // ── Identity ──
  setColor(doc, COLORS.ink, "text");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  const titleLines = doc.splitTextToSize(agent.name || "—", PAGE.width - PAGE.margin * 2);
  doc.text(titleLines, PAGE.margin, y);
  y += titleLines.length * 6 + 2;
  setColor(doc, COLORS.inkSecondary, "text");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`${agent.id} · ${agent.vendorName} · ${agent.model} · ${agent.version}`, PAGE.margin, y);
  y += 7;
  if (agent.purpose) {
    y = paragraph(doc, y, agent.purpose, { color: COLORS.ink, size: 10, lineHeight: 4.6 });
    y += 1;
  }

  // ── Ownership & Posture ──
  y = sectionHeading(doc, y + 2, "Ownership & Posture");
  y = kvRow(doc, y, "Owner", agent.ownerName);
  y = kvRow(doc, y, "Department · Business unit", `${agent.department || "—"}  ·  ${agent.businessUnit || "—"}`);
  y = kvRow(doc, y, "Status · Risk band", `${(agent.status || "—").toUpperCase()}  ·  ${(agent.riskBand || "—").toUpperCase()}`);
  y = kvRow(doc, y, "Autonomy", agent.autonomy || "—");
  y = kvRow(doc, y, "Deployed", agent.deployedAt || "—");
  y = kvRow(doc, y, "Composite risk", `${agent.riskScore ?? "—"} / 100`);

  // ── Model & Economics ──
  y = sectionHeading(doc, y + 4, "Model binding & monthly economics");
  y = kvRow(doc, y, "Vendor · Model", `${agent.vendorName || "—"}  ·  ${agent.model || "—"}`);
  y = kvRow(doc, y, "$ / 1k tokens", agent.per1kToken != null ? `A$${agent.per1kToken.toFixed(4)}` : "—");
  y = kvRow(doc, y, "Monthly spend", fmtAud(agent.monthlyCostAud));
  y = kvRow(doc, y, "Monthly value", fmtAud(agent.monthlyValueAud));
  const roi = agent.monthlyCostAud ? (agent.monthlyValueAud / agent.monthlyCostAud) : 0;
  y = kvRow(doc, y, "ROI multiple", `${roi.toFixed(1)}×`);

  // ── Policies bound ──
  y = sectionHeading(doc, y + 4, "Policies bound");
  const bound = (agent.policyIds || []).map((pid) => {
    const p = policies.find((x) => x.id === pid);
    return p ? `${pid} · ${p.name}` : pid;
  });
  if (bound.length === 0) {
    y = paragraph(doc, y, "No policies bound yet.", { color: COLORS.inkSecondary });
  } else {
    bound.forEach((line) => {
      setColor(doc, COLORS.ink, "text");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.text(`•  ${line}`, PAGE.margin + 1, y);
      y += 4.6;
    });
  }

  // ── Frameworks ──
  y = sectionHeading(doc, y + 4, "Framework coverage");
  y = chipsRow(doc, y + 1, agent.frameworks || (agent.framework ? [agent.framework] : []), COLORS.gold);

  // ── Lineage (Opportunity → DR → Agent) ──
  y = sectionHeading(doc, y + 4, "Lineage — opportunity → request → live agent");
  if (opportunity) {
    y = kvRow(doc, y, "Opportunity", `${opportunity.id} · ${opportunity.title || "—"}`);
  }
  if (request) {
    y = kvRow(doc, y, "Deployment request", `${request.id} · stage ${request.stage}/7 · ${request.status || "—"}`);
  }
  y = kvRow(doc, y, "Agent", `${agent.id} · ${agent.name}`);

  // ── Audit chain excerpt ──
  if (y > 240) {
    doc.addPage();
    y = PAGE.margin;
  }
  y = sectionHeading(doc, y + 4, "Audit chain · most recent 6 entries for this agent");
  const entries = (auditEntries || [])
    .filter((e) => (e.target || "").includes(agent.id) || (e.entityRef || "") === agent.id || (e.narrative || "").includes(agent.id))
    .slice(0, 6);
  if (entries.length === 0) {
    setColor(doc, COLORS.inkSecondary, "text");
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text("No audit entries reference this agent yet.", PAGE.margin, y);
    y += 6;
  } else {
    entries.forEach((e) => {
      setColor(doc, COLORS.inkDisabled, "text");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(`${e.ts || ""}   ·   ${e.actor || ""}   ·   hash ${e.hash || "—"}`, PAGE.margin, y);
      setColor(doc, COLORS.ink, "text");
      doc.setFontSize(9);
      const narrative = doc.splitTextToSize(e.narrative || e.action || "—", PAGE.width - PAGE.margin * 2);
      doc.text(narrative, PAGE.margin, y + 4);
      y += 4 + narrative.length * 4.2 + 2;
      if (y > 270) {
        doc.addPage();
        y = PAGE.margin;
      }
    });
  }

  // ── Signature stamp ──
  const signaturePayload = {
    agent: agent.id,
    tenant: tenant?.id || tenant?.tenant_id,
    generatedAt: now.toISOString(),
    policies: agent.policyIds || [],
    monthlyCostAud: agent.monthlyCostAud,
    monthlyValueAud: agent.monthlyValueAud,
  };
  const fp = await fingerprint(signaturePayload);

  const pageCount = doc.internal.pages.length - 1;
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    setColor(doc, COLORS.inkDisabled, "draw");
    doc.setLineWidth(0.1);
    doc.line(PAGE.margin, 287, PAGE.width - PAGE.margin, 287);
    setColor(doc, COLORS.inkDisabled, "text");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(`ALGOS · Agent Dossier  ·  ${agent.id}  ·  SHA-256 ${fp}`, PAGE.margin, 291);
    doc.text(`Page ${p} of ${pageCount}`, PAGE.width - PAGE.margin, 291, { align: "right" });
  }

  const filename = `dossier_${agent.id}_${now.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return { filename, fingerprint: fp };
}
